import React, { useState, useEffect, useRef, useContext } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { solarizedlight } from "react-syntax-highlighter/dist/esm/styles/prism";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import { FaUser, FaStar, FaCopy, FaShareAlt } from 'react-icons/fa';
import logo from "../../assets/logo.png";
import LoadingIndicator from "../loading-indicator/LoadingIndicator";
import { ChatContext } from "../../context/ChatContext";
import { trackInteraction } from "../../api/chatApi";
import { authFetch } from "../../utils/auth";
import styles from "./ChatMessage.module.css";

const ChatMessage = ({ messageId, conversationId, role, content, streamMessage, rating, isStreaming }) => {
  const [copyStatus, setCopyStatus] = useState({});
  const [currentRating, setCurrentRating] = useState(rating || 0);
  const [hover, setHover] = useState(null);
  const [messageCopyStatus, setMessageCopyStatus] = useState("Copy");
  const [isMessageCopyDisabled, setIsMessageCopyDisabled] = useState(false);
  const [shareStatus, setShareStatus] = useState("Share");
  const [isShareDisabled, setIsShareDisabled] = useState(false);
  const messageContentRef = useRef(null);
  const { error, status, statusType } = useContext(ChatContext);
  
  // Track last copy to prevent duplicate tracking when users hit Ctrl+C multiple times
  const lastCopyRef = useRef({ content: null, timestamp: 0, type: null });
  const COPY_DEBOUNCE_MS = 2000; // 2 seconds debounce window
  
  // Helper function to determine if we should track this copy event
  const shouldTrackCopy = (copyType, content) => {
    const now = Date.now();
    const timeSinceLastCopy = now - lastCopyRef.current.timestamp;
    
    // Track if:
    // 1. Content is different from last copy, OR
    // 2. More than 2 seconds have passed since last copy, OR
    // 3. Copy type is different (e.g., switched from text-selection to code-block)
    const shouldTrack = 
      lastCopyRef.current.content !== content ||
      timeSinceLastCopy > COPY_DEBOUNCE_MS ||
      lastCopyRef.current.type !== copyType;
    
    if (shouldTrack) {
      // Update last copy info
      lastCopyRef.current = {
        content: content,
        timestamp: now,
        type: copyType
      };
    }
    
    return shouldTrack;
  };

  useEffect(() => {
    const handleCopy = (e) => {
      const selection = window.getSelection();
      if (!selection.toString()) return;
      
      // Check if the selection is within this message component
      let isSelectionWithinThisMessage = false;
      const range = selection.getRangeAt(0);
      
      // Get the container of the current message
      const messageContainer = messageContentRef.current;
      if (!messageContainer) return;
      
      // Check if the selection is within this message component
      if (messageContainer.contains(range.commonAncestorContainer)) {
        isSelectionWithinThisMessage = true;
      } else {
        // Selection might start or end in this component
        try {
          const startContainer = range.startContainer;
          const endContainer = range.endContainer;
          isSelectionWithinThisMessage = 
            messageContainer.contains(startContainer) || 
            messageContainer.contains(endContainer);
        } catch (e) {
          // In case of any errors in the selection checking, don't track
          return;
        }
      }
      
      // Only process copy event if the selection is within this message
      if (!isSelectionWithinThisMessage) return;
      
      e.preventDefault();
        
      // Create a temporary container
      const tempDiv = document.createElement('div');
      
      // Clone the selected content with its formatting
      for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i);
        const clonedContent = range.cloneContents();
        tempDiv.appendChild(clonedContent);
      }
      
      // Remove background color from all elements in the temp container
      const allElements = tempDiv.getElementsByTagName('*');
      for (let el of allElements) {
        el.style.backgroundColor = 'transparent';
      }
      
      // Set both HTML and plain text
      e.clipboardData.setData('text/html', tempDiv.innerHTML);
      const selectedText = selection.toString();
      e.clipboardData.setData('text/plain', selectedText);
      
      // Track the selection copy event with the conversation ID and content (with debouncing)
      if (shouldTrackCopy('text-selection', selectedText)) {
        trackInteraction('text_copy', conversationId, messageId, selectedText, { copyType: 'text-selection' });
      }
    };

    document.addEventListener('copy', handleCopy);
    return () => document.removeEventListener('copy', handleCopy);
  }, []);

  const copyToClipboard = async (textToCopy, index) => {
    // Prevent clicking if already in "Copied!" state
    if (copyStatus[index] === "Copied!") {
      return;
    }
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopyStatus((prevStatus) => ({ ...prevStatus, [index]: "Copied!" }));
      
      // Track code block copy event with conversation ID and content (with debouncing)
      if (shouldTrackCopy('code-block', textToCopy)) {
        trackInteraction('text_copy', conversationId, messageId, textToCopy, { copyType: 'code-block' });
      }
    } catch (err) {
      setCopyStatus((prevStatus) => ({
        ...prevStatus,
        [index]: "Failed to copy text",
      }));
    }
  };

  const submitRating = async (ratingValue) => {
    try {
      setCurrentRating(ratingValue);
      const response = await authFetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rating: ratingValue, messageID: messageId }),
      });
      if (!response.ok) {
        throw new Error('Failed to submit rating');
      }
      console.log('Rating submitted successfully');
    } catch (error) {
      console.error('Error submitting rating:', error);
    }
  };

  useEffect(() => {
    const timers = Object.keys(copyStatus).map((index) => {
      if (copyStatus[index] === "Copied!") {
        return setTimeout(() => {
          setCopyStatus((prevStatus) => ({ ...prevStatus, [index]: "Copy" }));
        }, 2000);
      }
      return null;
    });

    return () => timers.forEach((timer) => timer && clearTimeout(timer));
  }, [copyStatus]);

  const copyEntireMessage = async () => {
    // Prevent clicking if button is already disabled
    if (isMessageCopyDisabled) {
      return;
    }
    
    try {
      if (!messageContentRef.current) return;

      // Create a temporary container
      const tempDiv = document.createElement('div');
      
      // Clone all the content except the message actions
      const contentElements = messageContentRef.current.querySelectorAll('[data-text-block], [data-code-block], [data-code-block-highlighter]');
      contentElements.forEach(element => {
        const clone = element.cloneNode(true);
        // Remove any copy buttons from code blocks
        const copyButtons = clone.querySelectorAll('[data-copy-btn]');
        copyButtons.forEach(btn => btn.remove());
        tempDiv.appendChild(clone);
      });

      // Remove background colors and preserve white-space
      const allElements = tempDiv.getElementsByTagName('*');
      for (let el of allElements) {
        el.style.backgroundColor = 'transparent';
        if (el.tagName === 'PRE' || el.tagName === 'CODE') {
          el.style.whiteSpace = 'pre';
        }
      }

      // Handle the copy operation
      const listener = (e) => {
        e.preventDefault();
        e.clipboardData.setData('text/html', tempDiv.innerHTML);
        const messageText = tempDiv.innerText;
        e.clipboardData.setData('text/plain', messageText);
        document.removeEventListener('copy', listener);
      };

      document.addEventListener('copy', listener);
      document.execCommand('copy');
      
      // Track full message copy event with conversation ID and content (with debouncing)
      if (shouldTrackCopy('full-message', content)) {
        trackInteraction('text_copy', conversationId, messageId, content, { copyType: 'full-message' });
      }
      
      setMessageCopyStatus("Copied!");
      setIsMessageCopyDisabled(true);
      setTimeout(() => {
        setMessageCopyStatus("Copy");
        setIsMessageCopyDisabled(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
      setMessageCopyStatus("Failed to copy");
      setIsMessageCopyDisabled(true);
      setTimeout(() => {
        setMessageCopyStatus("Copy");
        setIsMessageCopyDisabled(false);
      }, 2000);
    }
  };

  const shareConversation = async () => {
    if (isShareDisabled) return;

    try {
      const conversationUrl = `${window.location.origin}/c/${conversationId}#message-${messageId}`;

      // Use navigator.clipboard if available (requires HTTPS), otherwise fall back
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(conversationUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = conversationUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      // Track share interaction (no debounce — each share is intentional)
      trackInteraction('share', conversationId, messageId, content, { shareUrl: conversationUrl });

      setShareStatus("Link copied!");
      setIsShareDisabled(true);
      setTimeout(() => {
        setShareStatus("Share");
        setIsShareDisabled(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy conversation URL:', err);
      setShareStatus("Failed to share");
      setIsShareDisabled(true);
      setTimeout(() => {
        setShareStatus("Share");
        setIsShareDisabled(false);
      }, 2000);
    }
  };

  // Split the message into parts by code blocks
  const parts = content ? content.split(/(```[a-z]*\n?[\s\S]*?```)/i) : [];

  // Custom components for ReactMarkdown to open links in new tabs
  const markdownComponents = {
    a: ({node, children, ...props}) => (
      <a {...props} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    )
  };

  return (
    <div id={messageId ? `message-${messageId}` : undefined} className={styles['chat-message']}>
      <div className={styles['sender']}>
        {role === "user" ? (
          <>
            <FaUser className={styles['sender-icon']} />
            <span className={styles['sender-name']}>User</span>
          </>
        ) : (
          <>
            <img src={logo} alt='Logo' className={styles['sender-icon']} />
            <span className={styles['sender-name']}>COMPAS</span>
          </>
        )}
        {role !== "user" && (
          <span className={styles['star-rating']}>
            {[...Array(5)].map((star, index) => {
              const ratingValue = index + 1;
              return (
                <label key={index}>
                  <input
                    type='radio'
                    name={`rating-${messageId}`}
                    value={ratingValue}
                    onClick={() => submitRating(ratingValue)}
                  />
                  <FaStar
                    className={styles['star']}
                    color={
                      ratingValue <= (hover || currentRating) ? '#ffc107' : '#e4e5e9'
                    }
                    size={13}
                    onMouseEnter={() => setHover(ratingValue)}
                    onMouseLeave={() => setHover(null)}
                  />
                </label>
              );
            })}
          </span>
        )}
      </div>
      <div className={styles['message-content']} ref={messageContentRef}>
        {parts.map((part, index) => {
          // Check if this part is a code block
          const codeMatch = part.match(/```([a-z]*)\n?([\s\S]*?)```/i);
          if (codeMatch) {
            const language = codeMatch[1] ? codeMatch[1].trim() : null;
            const code = codeMatch[2].trim();
            return language ? (
              // If a language is specified, use the syntax highlighter
              <React.Fragment key={index}>
                <div className={styles['code-language']}>
                  <span>{language}</span>
                  <button
                    className={styles['copy-btn']}
                    data-copy-btn
                    onClick={() => copyToClipboard(code, index)}
                    disabled={copyStatus[index] === "Copied!"}
                  >
                    {copyStatus[index] || "Copy"}
                  </button>
                </div>
                <div className={styles['code-block-highlighter']} data-code-block-highlighter>
                  <SyntaxHighlighter language={language} style={solarizedlight}>
                    {code}
                  </SyntaxHighlighter>
                </div>
              </React.Fragment>
            ) : (
              // If no language is specified, use a simple code block
              <div className={styles['code-block']} data-code-block key={index}>
                <button
                  className={styles['copy-btn']}
                  data-copy-btn
                  onClick={() => copyToClipboard(code, index)}
                  disabled={copyStatus[index] === "Copied!"}
                >
                  {copyStatus[index] || "Copy"}
                </button>
                <pre key={index}>
                  <code>{code}</code>
                </pre>
              </div>
            );
          } else {
            // This part is regular text
            return (
              <div key={index} className={styles['text-block']} data-text-block>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{part}</ReactMarkdown>
              </div>
            );
          }
        })}
        {streamMessage && (
          <div className={styles['stream-message']}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{streamMessage}</ReactMarkdown>
          </div>
        )}
        {isStreaming && (
          <LoadingIndicator asMessage={false} error={error} status={status} statusType={statusType} />
        )}
        {role !== "user" && (
          <div className={styles['message-actions']}>
            <button 
              className={styles['copy-message-btn']}
              onClick={shareConversation}
              disabled={isShareDisabled}
            >
              <FaShareAlt className={styles['copy-icon']} />
              <span>{shareStatus}</span>
            </button>
            <button 
              className={styles['copy-message-btn']}
              onClick={copyEntireMessage}
              disabled={isMessageCopyDisabled}
            >
              <FaCopy className={styles['copy-icon']} />
              <span>{messageCopyStatus}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
